import { buildSearchIndex, normalize, searchAttributes } from '../search';
import { domIdFor, sectionKeyFor } from '../../../library/attributes-section/util';

const sections = [
  {
    sectionId: 'campground_details',
    sectionTitle: 'Campground Attributes',
    attributes: [
      {
        id: 'pull_through_sites',
        name: 'Pull-through sites',
        description: 'Sites you can drive straight through'
      },
      {
        id: 'showers',
        name: 'Showers',
        description: 'Shower facilities available'
      },
      {
        id: 'shower_count',
        name: 'Shower count',
        description: 'How many showers there are',
        depends_on: 'showers'
      },
      {
        id: 'orphan_count',
        name: 'Orphan count',
        description: 'Its parent lives in another section',
        depends_on: 'not_in_this_section'
      },
      {
        id: 'quiet_hours',
        name: 'Expected quiet hours available',
        description: 'When the campground enforces quiet'
      }
    ]
  },
  {
    sectionId: 'ev_station_details',
    sectionTitle: 'Electric Vehicle Details',
    attributes: [
      {
        id: 'network',
        name: 'Charging Network',
        description: 'Operator of the charger',
        options: [
          { id: 'chargepoint', name: 'ChargePoint Network' },
          { id: 'ampup', name: 'AMPUP' }
        ]
      },
      {
        id: 'cable_length',
        name: 'Cable length',
        description: 'How far the cable will pull'
      }
    ]
  }
];

const index = buildSearchIndex(sections);
const find = (key: string) => index.find((entry) => entry.key === key);

describe('normalize', () => {
  it('folds case and separators so hyphenated and underscored names match', () => {
    expect(normalize('Pull-through_Sites')).toBe('pull through sites');
  });

  it('collapses repeated whitespace and trims', () => {
    expect(normalize('  Shower   count  ')).toBe('shower count');
  });

  it('treats missing text as empty', () => {
    expect(normalize(null)).toBe('');
    expect(normalize(undefined)).toBe('');
  });
});

describe('sectionKeyFor', () => {
  it('slugifies a section title the same way the page anchors do', () => {
    expect(sectionKeyFor('Overnight RV Parking Attributes')).toBe(
      'overnight-rv-parking-attributes'
    );
  });
});

describe('domIdFor', () => {
  it('scopes the id by section so leaf names cannot collide', () => {
    expect(domIdFor('Restroom Attributes', 'restrooms')).toBe(
      'attr-restroom-attributes-restrooms'
    );
  });

  // Both parking sections share the sectionId 'parking_details', and the tabbed view
  // rewrites sectionId in place, so the title is what keeps these apart
  it('distinguishes the two sections that share a sectionId', () => {
    expect(domIdFor('Parking Attributes', 'price_rating')).not.toBe(
      domIdFor('Overnight RV Parking Attributes', 'price_rating')
    );
  });
});

describe('buildSearchIndex', () => {
  it('flattens every attribute across every section', () => {
    expect(index).toHaveLength(7);
  });

  // The page renders labels through formatLabel, so results have to read the same way
  it('labels entries the way the page renders them, not the raw config name', () => {
    expect(find('campground-attributes:quiet_hours').label).toBe('Quiet hours');
    expect(find('electric-vehicle-details:network').label).toBe('Charging network');
  });

  it('keys entries by section and attribute', () => {
    expect(find('campground-attributes:showers')).toBeDefined();
    expect(find('electric-vehicle-details:network')).toBeDefined();
  });

  it('points a dependent at its parent row, since it has no row of its own', () => {
    expect(find('campground-attributes:shower_count').domId).toBe(
      'attr-campground-attributes-showers'
    );
  });

  it('falls back to the attribute itself when its parent is not a sibling', () => {
    expect(find('campground-attributes:orphan_count').domId).toBe(
      'attr-campground-attributes-orphan_count'
    );
  });

  it('captures option names so a brand can be searched', () => {
    expect(find('electric-vehicle-details:network').options).toBe(
      'chargepoint network ampup'
    );
  });

  it('carries the section anchor for scrolling', () => {
    expect(find('campground-attributes:showers').anchorId).toBe('campground-attributes');
  });

  it('tolerates sections with no attributes', () => {
    expect(buildSearchIndex([])).toEqual([]);
    expect(buildSearchIndex([{ sectionId: 'empty' }])).toEqual([]);
  });
});

describe('searchAttributes', () => {
  it('returns nothing for an empty or whitespace query', () => {
    expect(searchAttributes(index, '')).toEqual({ results: [], total: 0 });
    expect(searchAttributes(index, '   ')).toEqual({ results: [], total: 0 });
  });

  it('matches across separators', () => {
    const { results, total } = searchAttributes(index, 'pull through');

    expect(total).toBe(1);
    expect(results[0].label).toBe('Pull-through sites');
  });

  it('ranks a name match above a description match', () => {
    const { results } = searchAttributes(index, 'pull');

    expect(results.map((entry) => entry.label)).toEqual([
      'Pull-through sites',
      'Cable length'
    ]);
  });

  it('requires every term to match, so more words narrow the results', () => {
    const { results, total } = searchAttributes(index, 'shower count');

    expect(total).toBe(1);
    expect(results[0].label).toBe('Shower count');
  });

  it('matches on section title', () => {
    const { total } = searchAttributes(index, 'electric');

    expect(total).toBe(2);
  });

  it('matches on option names', () => {
    const { results, total } = searchAttributes(index, 'chargepoint');

    expect(total).toBe(1);
    expect(results[0].label).toBe('Charging network');
  });

  it('matches the raw config name and the label shown on the page', () => {
    expect(searchAttributes(index, 'expected').total).toBe(1);
    expect(searchAttributes(index, 'quiet hours').total).toBe(1);
  });

  it('caps the results but reports the true total', () => {
    const { results, total } = searchAttributes(index, 'e', 1);

    expect(results).toHaveLength(1);
    expect(total).toBe(7);
  });

  it('returns nothing when no attribute matches', () => {
    expect(searchAttributes(index, 'zzzz')).toEqual({ results: [], total: 0 });
  });
});
